import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { logout, useCurrentUser, useIsMetaReviewer } from '../../lib/session';
import { pendingDraftCount } from '../../lib/drafts';
import { Avatar } from '../../ui/Avatar';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { PoweredBy } from '../../ui/PoweredBy';
import type { ThemePreference } from '../../lib/theme';
import { IconBoard, IconClock, IconLogout, IconMonitor, IconMoon, IconPlug, IconSun, IconUsers } from '../../ui/icons';
import { useTheme } from '../../ui/useTheme';
import styles from './UserMenu.module.css';

const ROLE_LABEL = { ADMIN: 'Administración', SUPERVISOR: 'Supervisión', AGENT: 'Atención' } as const;

const APPEARANCE: Array<{ value: ThemePreference; label: string; icon: ReactNode }> = [
  { value: 'light', label: 'Claro', icon: <IconSun /> },
  { value: 'dark', label: 'Oscuro', icon: <IconMoon /> },
  { value: 'schedule', label: 'Según la hora', icon: <IconClock /> },
  { value: 'system', label: 'Sistema', icon: <IconMonitor /> },
];

/** Avatar de la persona con su menú: quién eres y cerrar sesión. */
export function UserMenu() {
  const client = useQueryClient();
  const user = useCurrentUser();
  const metaReviewer = useIsMetaReviewer();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<{ drafts: number } | null>(null);
  const [leaving, setLeaving] = useState(false);
  const { preference, setPreference } = useTheme();

  const askLogout = () => {
    setOpen(false);
    setConfirming({ drafts: pendingDraftCount() });
  };
  const confirmLogout = () => {
    setLeaving(true);
    void logout(client);
  };
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const options = useRef<Array<HTMLButtonElement | null>>([]);

  /**
   * Cancelar el cierre de sesión: el botón que abrió el diálogo ya no existe, así que el foco vuelve a la
   * cuenta. Va en un efecto porque el del diálogo (hijo, se ejecuta antes) llama a close(), y el navegador
   * devolvería el foco al elemento desmontado, es decir, a <body>.
   */
  const refocusTrigger = useRef(false);
  const cancelLogout = () => {
    refocusTrigger.current = true;
    setConfirming(null);
  };
  useEffect(() => {
    if (confirming !== null || !refocusTrigger.current) return;
    refocusTrigger.current = false;
    trigger.current?.focus();
  }, [confirming]);

  // Se cierra al pulsar fuera o con Escape; con Escape el foco vuelve al botón de la cuenta (C8).
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Grupo de radios (WAI-ARIA): las flechas, Inicio y Fin mueven el foco y eligen; Tab entra solo en el elegido.
  const selected = Math.max(0, APPEARANCE.findIndex((option) => option.value === preference));
  const onRadioKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    const target = event.key === 'Home' ? 0 : event.key === 'End' ? APPEARANCE.length - 1
      : step === undefined ? null : (selected + step + APPEARANCE.length) % APPEARANCE.length;
    if (target === null) return;
    event.preventDefault();
    setPreference(APPEARANCE[target]!.value);
    options.current[target]?.focus();
  };

  return (
    <div className={styles.root} ref={root}>
      <button ref={trigger} type="button" className={styles.trigger} aria-expanded={open} aria-controls={menuId}
        aria-label={`Tu cuenta: ${user.displayName}`} onClick={() => setOpen((current) => !current)}>
        <Avatar name={user.displayName} seed={user.id} size="sm" />
      </button>
      {open && (
        <div id={menuId} className={styles.menu}>
          <div className={styles.who}>
            <Avatar name={user.displayName} seed={user.id} />
            <div className={styles.whoText}>
              <span className={styles.name}>{user.displayName}</span>
              <span className={styles.email}>{user.email}</span>
              <span className={styles.role}>{ROLE_LABEL[user.role]}</span>
            </div>
          </div>
          <div className={styles.appearance}>
            <span className={styles.sectionLabel} id={`${menuId}-apariencia`}>Apariencia</span>
            <div className={styles.options} role="radiogroup" aria-labelledby={`${menuId}-apariencia`} onKeyDown={onRadioKey}>
              {APPEARANCE.map((option, index) => (
                <button key={option.value} ref={(element) => { options.current[index] = element; }} type="button" role="radio"
                  aria-checked={preference === option.value} tabIndex={index === selected ? 0 : -1}
                  className={styles.option} onClick={() => setPreference(option.value)}>
                  {option.icon}
                  {option.label}
                </button>
              ))}
            </div>
            <p className={styles.appearanceNote}>«Según la hora» usa el modo oscuro de 19:00 a 6:00.</p>
          </div>
          {!metaReviewer && (
            <Link to="/tablero" className={styles.action} onClick={() => setOpen(false)}>
              <IconBoard />
              Tablero
            </Link>
          )}
          {user.role === 'ADMIN' && (
            <Link to="/conexiones" className={styles.action} onClick={() => setOpen(false)}>
              <IconPlug />
              Conexiones
            </Link>
          )}
          {user.role === 'ADMIN' && (
            <Link to="/equipo" className={styles.action} onClick={() => setOpen(false)}>
              <IconUsers />
              Equipo
            </Link>
          )}
          <button type="button" className={styles.action} onClick={askLogout}>
            <IconLogout />
            Cerrar sesión
          </button>
          <div className={styles.credit}><PoweredBy /></div>
        </div>
      )}
      <ConfirmDialog open={confirming !== null} icon={<IconLogout />} title="¿Cerrar sesión?"
        confirmLabel="Cerrar sesión" busyLabel="Cerrando…" busy={leaving}
        onConfirm={confirmLogout} onCancel={cancelLogout}>
        <p>Tendrás que volver a entrar con tu correo y tu contraseña.</p>
        {confirming && confirming.drafts > 0 && (
          <p className={styles.draftWarning}>
            {confirming.drafts === 1
              ? 'Tienes 1 respuesta escrita sin enviar: se perderá.'
              : `Tienes ${confirming.drafts} respuestas escritas sin enviar: se perderán.`}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
